import { clsx } from 'clsx';
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { Image } from 'expo-image';
import Animated, { FadeIn, FadeOut } from 'react-native-reanimated';
import { StyleSheet, View } from 'react-native';

import { useIsSmallDevice } from '@/hooks/use-small-device';
import { getDemoVideoUrl, getDemoThumbnailUrl } from '@/config/media';

const VideoComponent: React.FC<{
  name: string;
  enteringAnimationDelay?: number;

  width?: number;
}> = React.memo(
  ({
    name,
    enteringAnimationDelay = 0,

    width = 350,
  }) => {
    const [isHovered, setIsHovered] = useState(false);
    const [hasBeenHovered, setHasBeenHovered] = useState(false);
    const isSmallDevice = useIsSmallDevice();

    const handleMouseEnter = useCallback(() => {
      setIsHovered(true);
      setHasBeenHovered(true);
    }, []);

    const handleMouseLeave = useCallback(() => {
      setIsHovered(false);
    }, []);

    const thumbnailSrc = useMemo(() => getDemoThumbnailUrl(name), [name]);

    return (
      <div
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className="relative overflow-hidden rounded-xl flex flex-1 select-none"
        style={{
          width,
          height: '100%',
          aspectRatio: '340/770',
        }}
      >
        <Animated.View
          entering={FadeIn.duration(300).delay(enteringAnimationDelay)}
          exiting={FadeOut.duration(600)}
          style={StyleSheet.absoluteFill}
        >
          <View
            className={clsx(
              'z-10 absolute inset-0 top-0 left-0 bottom-0 right-0 duration-300'
            )}
          >
            <Image
              source={thumbnailSrc}
              alt={name + ' poster'}
              style={styles.image}
            />
          </View>
        </Animated.View>
        {(isSmallDevice || hasBeenHovered) && (
          <InternalVideoComponent
            src={getDemoVideoUrl(name)}
            poster={getDemoThumbnailUrl(name)}
            isSmallDevice={isSmallDevice}
            isHovered={isHovered}
          />
        )}
      </div>
    );
  }
);

const styles = StyleSheet.create({
  image: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
});

const InternalVideoComponent: React.FC<{
  src: string;
  poster: string;
  isSmallDevice: boolean;
  isHovered: boolean;
}> = React.memo(({ src, poster, isSmallDevice, isHovered }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const timeoutRef = useRef<number>(null);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      const handleLoaded = () => setIsLoaded(true);
      video.addEventListener('loadeddata', handleLoaded);
      return () => video.removeEventListener('loadeddata', handleLoaded);
    }
    return;
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: isSmallDevice ? 0.9 : 0.75,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const video = entry.target as HTMLVideoElement;

        if (entry.isIntersecting) {
          video.muted = true;
          video.playsInline = true;

          // Clear any existing timeout
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
          }

          timeoutRef.current = window.setTimeout(() => {
            const playPromise = video.play();
            if (playPromise !== undefined) {
              playPromise.catch(() => {
                // If it fails, try one more time
                video
                  .play()
                  .catch((e) => console.warn('Final play attempt failed:', e));
              });
            }
          }, 0);
        } else {
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
          }
          video.pause();
        }
      });
    }, options);

    if (video) {
      observer.observe(video);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (video) {
        observer.unobserve(video);
      }
    };
  }, [isSmallDevice]);

  const videoClassName = useMemo(() => {
    return clsx(
      'bg-background top-0 left-0 w-full h-full object-cover select-none',
      isSmallDevice ? 'relative' : 'absolute',

      isHovered && isLoaded
        ? 'opacity-100'
        : isSmallDevice && isLoaded
          ? 'opacity-100'
          : 'opacity-0',
      'transition-all duration-300'
    );
  }, [isHovered, isLoaded, isSmallDevice]);

  const videoStyle = useMemo(
    () => ({
      WebkitUserSelect: 'none' as const,
      userSelect: 'none' as const,
    }),
    []
  );

  return (
    <video
      ref={videoRef}
      preload="none"
      loop
      muted
      playsInline
      draggable="false"
      webkit-playsinline="true"
      style={videoStyle}
      className={videoClassName}
      poster={poster}
    >
      <source src={src} type="video/mp4" />
    </video>
  );
});

InternalVideoComponent.displayName = 'InternalVideoComponent';
VideoComponent.displayName = 'VideoComponent';

export { VideoComponent };
